import { FC } from 'react'
import { Box, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IFacilityFilters } from './NewsFacilityFilter.action'
import { ILocality, ILocalityGroup } from 'src/generated-types'
import { IStorage } from 'src/generated-types/storage-types'
import { IServiceStation } from 'src/generated-types/service-station'

export interface ISelectedFiltersProps {
  facilityFilters?: IFacilityFilters
  localitiesData?: ILocality[]
  serviceStationsData?: IServiceStation[]
  storagesData?: IStorage[]
  localityGroups?: ILocalityGroup[]
  onFilterChange: (filters: IFacilityFilters | undefined) => void
}

interface FilterGroupConfig {
  selectedIds: number[]
  allItems: Array<{ id: number; name: string }>
  allLabel: string
  itemLabel: (id: number, name: string) => string
  onClear: () => void
  onRemove: (id: number) => void
  keyPrefix: string
}

const SelectedFilters: FC<ISelectedFiltersProps> = ({
  facilityFilters,
  localitiesData,
  serviceStationsData,
  storagesData,
  localityGroups,
  onFilterChange,
}) => {
  const { t: translate } = useTranslation(['common'])

  const groups: FilterGroupConfig[] = [
    {
      selectedIds: facilityFilters?.localityIds ?? [],
      allItems: localitiesData ?? [],
      allLabel: translate('all-localities', { ns: 'common' }),
      itemLabel: (_id, name) => name,
      onClear: () => onFilterChange({ ...facilityFilters, localityIds: [] }),
      onRemove: id =>
        onFilterChange({ ...facilityFilters, localityIds: facilityFilters?.localityIds?.filter(x => x !== id) }),
      keyPrefix: 'locality',
    },
    {
      selectedIds: facilityFilters?.serviceStationIds ?? [],
      allItems: serviceStationsData ?? [],
      allLabel: translate('all-service-stations', { ns: 'common' }),
      itemLabel: (_id, name) => name,
      onClear: () => onFilterChange({ ...facilityFilters, serviceStationIds: [] }),
      onRemove: id =>
        onFilterChange({
          ...facilityFilters,
          serviceStationIds: facilityFilters?.serviceStationIds?.filter(x => x !== id),
        }),
      keyPrefix: 'ss',
    },
    {
      selectedIds: facilityFilters?.storageIds ?? [],
      allItems: storagesData ?? [],
      allLabel: translate('all-storages', { ns: 'common' }),
      itemLabel: (_id, name) => name,
      onClear: () => onFilterChange({ ...facilityFilters, storageIds: [] }),
      onRemove: id =>
        onFilterChange({ ...facilityFilters, storageIds: facilityFilters?.storageIds?.filter(x => x !== id) }),
      keyPrefix: 'storage',
    },
    {
      selectedIds: facilityFilters?.localityGroupIds ?? [],
      allItems: localityGroups ?? [],
      allLabel: translate('all-locality-groups', { ns: 'common' }),
      itemLabel: (_id, name) => name,
      onClear: () => onFilterChange({ ...facilityFilters, localityGroupIds: [] }),
      onRemove: id =>
        onFilterChange({
          ...facilityFilters,
          localityGroupIds: facilityFilters?.localityGroupIds?.filter(x => x !== id),
        }),
      keyPrefix: 'lg',
    },
  ]

  const hasFilters = groups.some(g => g.selectedIds.length > 0)
  if (!hasFilters) return null

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
      {groups.map(({ selectedIds, allItems, allLabel, itemLabel, onClear, onRemove, keyPrefix }) => {
        if (selectedIds.length === 0) return null

        const allSelected = allItems.length > 0 && allItems.every(item => selectedIds.includes(item.id))

        if (allSelected) {
          return <Chip key={`${keyPrefix}-all`} label={allLabel} onDelete={onClear} size="small" color="primary" />
        }

        return selectedIds.map(id => {
          const item = allItems.find(i => i.id === id)
          return (
            <Chip
              key={`${keyPrefix}-${id}`}
              label={itemLabel(id, item?.name ?? String(id))}
              onDelete={() => onRemove(id)}
              size="small"
              color="primary"
            />
          )
        })
      })}
    </Box>
  )
}

export default SelectedFilters
